import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import type { QuestionOption } from "@/types/assessment";
import { CheckIcon, XIcon } from "lucide-react";

interface SingleChoiceInputProps {
  options: QuestionOption[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  // After submission, show which answers were correct
  showResults?: boolean;
  correctOptionId?: string;
  selectedOptionId?: string;
}

export function SingleChoiceInput({
  options,
  value,
  onChange,
  disabled = false,
  showResults = false,
  correctOptionId,
  selectedOptionId,
}: SingleChoiceInputProps) {
  return (
    <RadioGroup
      // Pass an empty string (not undefined) when no option is selected so
      // Radix stays in controlled mode for the component's lifetime. Passing
      // `undefined` puts Radix in uncontrolled mode on first render, then
      // switching to a real value on first click forces an internal remount
      // which detaches the focused element and the browser teleports the
      // viewport to scrollY=0. React's "decide controlled vs uncontrolled"
      // warning fires on this transition — that's the smoking gun.
      value={value ?? ""}
      onValueChange={onChange}
      disabled={disabled}
      className="space-y-2"
    >
      {options.map((option) => {
        const optionId = String(option.id);
        const isSelected = selectedOptionId === optionId;
        const isCorrect = correctOptionId === optionId;
        const wasWrongChoice = showResults && isSelected && !isCorrect;
        const showCorrectIndicator = showResults && isCorrect;

        return (
          <label
            key={optionId}
            className={cn(
              "flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all",
              "hover:bg-accent/50",
              disabled && "cursor-not-allowed opacity-60",
              // Selected state (before submission)
              !showResults && value === optionId && "border-primary bg-accent/30",
              // After submission states
              showCorrectIndicator && "border-green-500 bg-green-50 dark:bg-green-950/30",
              wasWrongChoice && "border-red-500 bg-red-50 dark:bg-red-950/30"
            )}
          >
            <RadioGroupItem value={optionId} id={optionId} />
            <span className="flex-1">{option.content}</span>
            {showResults && (
              <span className="flex-shrink-0">
                {showCorrectIndicator && (
                  <CheckIcon className="size-5 text-green-600" />
                )}
                {wasWrongChoice && (
                  <XIcon className="size-5 text-red-600" />
                )}
              </span>
            )}
          </label>
        );
      })}
    </RadioGroup>
  );
}
